import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './ui/Modal';
import VaultPassphraseInput, { VaultPassphraseStrength } from './VaultPassphraseInput';
import VaultRecoveryPhraseStep from './VaultRecoveryPhraseStep';
import {
  MIN_PASSPHRASE_CHARS,
  nfc,
  passphraseProblem,
  pwStrength,
} from './vaultPassphraseUtils';
import { UNLOCK_PASSPHRASE_ERROR } from './vaultPassphraseCopy';
import { vaultErrorToken, refreshVaultMaster } from '../store/vaultStore';
import { prepareRekey, commitRekey, rekeyErrorText } from './vaultRekey';
import './Vault.css';

/**
 * Change the master passphrase — CONTRACT §6.4, Standalone vault card 2.4.
 *
 * Since card 2.4 a change is a **re-key** (`vaultRekey.js`): the vault gets a
 * new key, every item is re-encrypted on this computer, new 12 words replace
 * the old ones, and other devices ask for the new passphrase. The paragraphs
 * below describe the retired rewrap and are kept for the history of the copy
 * rules (no string may promise more than the implementation does).
 *
 * A change was a **rewrap**: one ~48-byte record is rewritten and not a single
 * item is re-encrypted, which is why the copy promises it is instant however
 * large the vault is. That sentence is a contract with the Rust side (hazard
 * H4) — if this screen ever renders slowly on a big vault, the implementation
 * has been "simplified" into a re-encrypt and an offline device's pending
 * writes are about to become undecryptable.
 *
 * The consequence the amber block exists to state: a rewrap is **not** a
 * revocation. `vault.json.bak` is overwritten and the epoch is bumped, but
 * anyone who already opened the vault has a copy of what was inside it, and
 * there is no vault sync yet to fence other devices with. Nothing on this
 * screen may imply otherwise.
 *
 * Retyping the current passphrase is required even though the vault is already
 * unlocked: an unlocked session is not proof that the person at the keyboard
 * knows the secret. The one exception is State 13 — both secrets forgotten but
 * this machine still opens the vault automatically — where Rust accepts an
 * empty `current` if and only if the device cache verifies. That path is the
 * highest-value data-loss mitigation in the design, so it gets a visible door.
 *
 * **The recovery rotation is two-phase**, exactly like first-time setup. Rust
 * returns the new twelve words WITHOUT having written their wrap; this screen
 * shows them, and only `Done` — via `vault_master_recovery_commit` — retires
 * the old phrase. Committing first would erase the old wrap before the new
 * words had reached anyone, so a crash, a force-quit or a render error in that
 * window left the user with no recovery phrase at all and no way to know. Not
 * committing is the safe direction: the old phrase simply keeps working.
 */
/** Commit errors after which Rust re-parks the prepared re-key (`commit_core`). */
const RETRYABLE_COMMIT = new Set([
  'rekey_reauth_required',
  'rekey_reauth_cancelled',
  'rekey_second_factor_required',
  'rekey_sync_pending',
]);

export default function VaultPassphraseChangeModal({
  isOpen,
  onClose,
  /** Enables the State-13 door. True only when this machine auto-unlocks. */
  remembered = false,
  /** Drives the amber "change your stored passwords too" block. */
  itemCount = 0,
}) {
  const { t } = useTranslation();

  const [current, setCurrent] = useState('');
  const [useCache, setUseCache] = useState(false);
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [newWords, setNewWords] = useState(null);
  // The parked rotation. While this is set the OLD phrase is still the live
  // one; nothing about the new words is durable until `Done`.
  const [recoveryHandle, setRecoveryHandle] = useState(null);

  const reset = useCallback(() => {
    setCurrent('');
    setUseCache(false);
    setNext('');
    setConfirm('');
    setBusy(false);
    setError('');
    setDone(false);
    setNewWords(null);
    setRecoveryHandle(null);
  }, []);

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  const problem = passphraseProblem(next, confirm);
  const showTooShort = next !== '' && [...nfc(next)].length < MIN_PASSPHRASE_CHARS;
  const showMismatch = confirm !== '' && problem === 'mismatch';
  const showEdgeSpace = problem === 'edgeSpace';
  const showRepetitive = problem === 'tooRepetitive';
  const showWeak = !showTooShort && next !== '' && pwStrength(nfc(next)) <= 2;
  const currentReady = useCache || current !== '';

  const errorText = (e) => {
    const rekey = rekeyErrorText(t, e);
    if (rekey) return rekey;
    switch (vaultErrorToken(e)) {
      case 'bad_passphrase':
        // Byte-identical to the unlock screen's string, on purpose.
        return t('vault.unlockFailed', UNLOCK_PASSPHRASE_ERROR);
      case 'passphrase_reused':
        return t(
          'vault.errReused',
          'Choose a passphrase you have not used here before.'
        );
      case 'passphrase_weak':
        return t('vault.errWeak', 'That passphrase is too common. Choose something else.');
      case 'passphrase_mismatch':
        return t('vault.errMismatch', 'Both entries must match.');
      case 'master_not_set':
        return t('vault.errNotSet', 'This vault does not have a passphrase yet.');
      case 'master_changed_elsewhere':
        return t(
          'vault.errChangedElsewhere',
          'The vault passphrase changed somewhere else while this was open. Close this and start again.'
        );
      case 'vault_update_required':
        // Card 1.1: a newer keyblock exists; nothing was rewritten.
        return t(
          'vault.updateRequiredBlocked',
          'This vault was saved by a newer version of Apiboo. Update Apiboo first — nothing was changed.'
        );
      case 'handle_expired':
        // Reachable only on the rotation commit, where the honest news is that
        // nothing was lost: the wrap was never rewritten.
        return t(
          'vault.errRotateExpired',
          'This took too long, so the new words were not saved. Your previous recovery phrase still works.'
        );
      default:
        return t('vault.errGeneric', 'That did not work. Try again.');
    }
  };

  const handleChange = async () => {
    if (busy || problem || !currentReady) return;
    setBusy(true);
    setError('');
    try {
      // Card 2.4: nothing is written yet — the words screen's Done commits.
      const result = await prepareRekey({ current: useCache ? '' : current, next, confirm });
      setCurrent('');
      setNext('');
      setConfirm('');
      setNewWords(result.words);
      setRecoveryHandle(result.handle);
    } catch (e) {
      setError(errorText(e));
    }
    setBusy(false);
  };

  // `Done` is the commit, not an acknowledgement: until it succeeds the OLD
  // twelve words are still the live ones. Undismissable for the same reason
  // step 2 of setup is — the words are on screen exactly once, and leaving
  // without them must not be one stray Escape away.
  const commitRotation = async () => {
    if (busy) return;
    if (!recoveryHandle) {
      setNewWords(null);
      if (onClose) onClose();
      return;
    }
    setBusy(true);
    setError('');
    try {
      await commitRekey(recoveryHandle);
      setRecoveryHandle(null);
      setNewWords(null);
      setDone(true);
      await refreshVaultMaster();
      window.dispatchEvent(new Event('vault-master-changed'));
    } catch (e) {
      setError(errorText(e));
      // Only these keep the prepared change parked in Rust (fix it, press
      // Done again). After any other error the handle is gone, so the next
      // Done closes instead of failing with handle_expired forever — the
      // words screen cannot be dismissed otherwise (code review of #475).
      if (!RETRYABLE_COMMIT.has(vaultErrorToken(e))) setRecoveryHandle(null);
    }
    setBusy(false);
  };

  if (newWords) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={() => {}}
        preventBackdropClose
        preventEscapeClose
        size="xl"
        contentClassName="vault-pass-modal--nodismiss"
        title={t('vault.recoveryTitle', 'Save your recovery phrase')}
      >
        <VaultRecoveryPhraseStep
          words={newWords}
          busy={busy}
          error={error}
          headline={
            <p className="vault-pass-text">
              {t(
                'vault.changeRotatedHeadline',
                'Write these down: they are your new recovery words. Your passphrase changes when you press Done — until then the old passphrase and the old 12 words keep working.'
              )}
            </p>
          }
          onDone={commitRotation}
        />
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={busy ? () => {} : onClose}
      size="xl"
      title={t('vault.changeTitle', 'Change your vault passphrase')}
    >
      <div className="vault-pass-body">
        {done ? (
          <>
            {/* States what happened, and nothing more. The old string here
                promised that other devices would be fenced, which the
                implementation explicitly does not do (`vault.rs`: "No UI string
                may promise revocation") — a rewrap does not move the DEK and
                there is no vault sync yet. A user rotating because someone
                learned the old passphrase would read that, conclude the other
                machines were locked out, and skip the one mitigation that
                actually works. */}
            <div className="vault-share-success">
              {t(
                'vault.rekeySuccess',
                'Passphrase changed. Your vault has a new key: the old passphrase and the old 12 words no longer open it. Your other devices ask for the new passphrase. Anyone who already opened this vault still has a copy of what was inside it — change the passwords stored here as well.'
              )}
            </div>
            <div className="modal-footer-row">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
              >
                {t('common.close')}
              </button>
            </div>
          </>
        ) : (
          <>
            {!useCache ? (
              <>
                <VaultPassphraseInput
                  id="vault-passphrase-current"
                  label={t('vault.changeCurrent', 'Current passphrase')}
                  value={current}
                  onChange={setCurrent}
                  autoComplete="current-password"
                  autoFocus
                  disabled={busy}
                />
                {remembered && (
                  <button
                    type="button"
                    className="vault-pass-linkbtn"
                    onClick={() => {
                      setUseCache(true);
                      setCurrent('');
                      setError('');
                    }}
                  >
                    {t(
                      'vault.changeUseThisComputer',
                      "I do not remember it — this computer opens the vault automatically"
                    )}
                  </button>
                )}
              </>
            ) : (
              <div className="vault-pass-banner">
                <span className="vault-pass-banner-title">
                  {t('vault.changeCacheTitle', 'Using this computer as proof')}
                </span>
                <span className="vault-pass-banner-text">
                  {t(
                    'vault.changeCacheBody',
                    'This computer can already open the vault, so it can set a new passphrase without the old one. Do this now — if this computer is reset or replaced first, nothing can open the vault again.'
                  )}
                </span>
                <button
                  type="button"
                  className="vault-pass-linkbtn"
                  onClick={() => setUseCache(false)}
                >
                  {t('vault.changeTypeCurrentInstead', 'Type my current passphrase instead')}
                </button>
              </div>
            )}

            <VaultPassphraseInput
              id="vault-passphrase-next"
              label={t('vault.changeNew', 'New passphrase (at least 12 characters)')}
              value={next}
              onChange={setNext}
              autoComplete="new-password"
              disabled={busy}
            />
            <VaultPassphraseStrength value={next} />

            <VaultPassphraseInput
              id="vault-passphrase-next-confirm"
              label={t('vault.changeConfirm', 'Confirm new passphrase')}
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleChange();
              }}
            />

            {showTooShort && (
              <span className="vault-modal-error">
                {t('vault.errTooShort', 'Use at least 12 characters.')}
              </span>
            )}
            {showRepetitive && (
              <span className="vault-modal-error">
                {t('vault.errRepetitive', 'Use at least 5 different characters — a repeated pattern is the first thing an attacker tries.')}
              </span>
            )}
            {showEdgeSpace && (
              <span className="vault-modal-error">
                {t(
                  'vault.errEdgeSpace',
                  'Remove the space at the start or end — a space you cannot see is a passphrase you cannot retype.'
                )}
              </span>
            )}
            {showMismatch && (
              <span className="vault-modal-error">
                {t('vault.errMismatch', 'Both entries must match.')}
              </span>
            )}
            {showWeak && (
              <p className="vault-pass-hint">
                {t(
                  'vault.setupWeakAdvisory',
                  'This passphrase is weak. Anyone who copies your vault file can guess at it offline, as fast as their hardware allows.'
                )}
              </p>
            )}


            {/* Neutral: nothing is destroyed, and the speed is the proof. */}
            <div className="vault-pass-banner">
              <span className="vault-pass-banner-title">
                {t('vault.changeWhatChangesTitle', 'What changes and what does not')}
              </span>
              <span className="vault-pass-banner-text">
                {t(
                  'vault.rekeyWhatChanges1',
                  'Your vault gets a new key. Every item is re-encrypted on this computer and stays exactly as it is.'
                )}
              </span>
              {/* Same rule as the success line: say what a re-key does, never
                  more (card R2.10's honest limits). */}
              <span className="vault-pass-banner-text">
                {t(
                  'vault.rekeyWhatChanges2',
                  'You get 12 new recovery words. The old passphrase and the old words stop opening this vault, here and on your other devices.'
                )}
              </span>
            </div>

            {/* Required whenever the vault holds anything: without it the screen
                implies a rewrap is a revocation, which is false. */}
            {itemCount >= 1 && (
              <div className="vault-pass-caution">
                {t(
                  'vault.changeCompromiseWarning',
                  'If you are changing this because someone else may have learned your passphrase, change the passwords stored in this vault as well. Anyone who already opened your vault has a copy of what was inside it.'
                )}
                {/* Card R2.10: the honest limit of a re-key. */}
                <span className="vault-pass-caution-more">
                  {t(
                    'vault.rekeyHonestLimit',
                    'A new key protects against copies taken from now on. It cannot undo copies made before: an old export, or an earlier copy of your encrypted vault together with your old passphrase, still opens what it held — on your own devices and, in the worst case, on a server that kept old data. Change the passwords at the websites too.'
                  )}
                </span>
              </div>
            )}

            {error && <div className="vault-modal-error">{error}</div>}

            <div className="modal-footer-row">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleChange}
                disabled={busy || !!problem || !currentReady}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
              >
                {busy
                  ? t('vault.changeBusy', 'Changing…')
                  : t('vault.changePrimary', 'Change passphrase')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
